import { createElement, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useTheme } from '@emotion/react';
import type { Action } from 'kbar';
import {
  CloudOutlined,
  CodeOutlined,
  DashboardOutlined,
  DatabaseOutlined,
  HomeOutlined,
  ReadOutlined,
  SettingOutlined,
  SnippetsOutlined
} from '@ant-design/icons';
import TemplateIcon from 'assets/images/template-icon.svg';
import DocsIcon from 'assets/images/docs-icon.svg';
import VariableIcon from 'assets/images/variables-icon.svg';
import DashboardIcon from 'assets/images/graph-icon.svg';
import ModulesIcon from 'assets/images/terraform-module-icon.svg';
import SettingsIcon from 'assets/images/settings-icon.svg';
import CloudInsightsIcon from 'assets/images/cloud-insights-bw.svg';
import HomepageIcon from 'assets/images/org-homepage-bw.svg';

import capitalize from 'lodash/capitalize';
import { ORGANIZATION_SETTINGS_ROOT_ID } from 'hooks/kbar/use-settings-tabs-actions.hooks';
import { useHasPermissionCallback } from 'hooks/use-has-permission';
import type { RolesApi } from '@env0/role-service/api';
import compact from 'lodash/compact';

type RBACPermission = RolesApi.RBACPermission;

type Navigate = (path: string) => void;

const section = 'Organization';

const docsAction = {
  id: 'docs',
  name: 'env0 docs',
  keywords: 'docs',
  section: 'docs',
  perform: () => window.open('https://docs.envzero.com/'),
  icon: DocsIcon
};

const organizationSettingsBasicAction: Action = {
  id: ORGANIZATION_SETTINGS_ROOT_ID,
  name: 'Settings',
  keywords: 'settings',
  section,
  icon: SettingsIcon
};

const generateCreatableEntityActions = ({
  navigate,
  sectionName,
  icon,
  urlPrefix = ''
}: {
  navigate: Navigate;
  sectionName: string;
  icon: Action['icon'];
  urlPrefix?: string;
}): Action[] => {
  const sectionAction: Action = {
    id: `GENERAL_${sectionName.toUpperCase()}_SECTION`,
    name: sectionName,
    keywords: sectionName,
    icon,
    section
  };

  const viewAction: Action = {
    id: `GOTO_${sectionName.toUpperCase()}_PAGE`,
    parent: sectionAction.id,
    name: `Go to ${sectionName}`,
    keywords: `Go to ${sectionName}`,
    perform: () => navigate(`${urlPrefix}/${sectionName.toLowerCase()}`),
    icon,
    section: sectionName
  };
  const createNewAction: Action = {
    id: `CREATE_NEW_${sectionName.toUpperCase()}`,
    parent: sectionAction.id,
    name: `Create new ${sectionName}`,
    keywords: `Create new ${sectionName}`,
    perform: () =>
      navigate(`${urlPrefix}/${sectionName.toLowerCase()}/add-new-${sectionName.toLowerCase().slice(0, -1)}`),
    icon,
    section: sectionName
  };

  return [sectionAction, viewAction, createNewAction];
};

const generateTemplatesSectionActions = (navigate: Navigate, icon: Action['icon']): Action[] =>
  generateCreatableEntityActions({ navigate, sectionName: 'Templates', icon });
const generateModulesSectionActions = (navigate: Navigate, icon: Action['icon']): Action[] =>
  generateCreatableEntityActions({ navigate, sectionName: 'Modules', icon, urlPrefix: '/registry' });

export default function useBasicNavigationActions(): Action[] {
  const navigate = useNavigate();
  const hasPermission = useHasPermissionCallback();
  const { isNewDesign } = useTheme();

  return useMemo(() => {
    /* The sidebar's own line icons (navbar-organization-menu), so the palette matches the nav beside
       it; legacy keeps its multicolour SVG set (UI-polish review). createElement because this is a
       .ts module. */
    const icons: Record<string, Action['icon']> = isNewDesign
      ? {
          docs: createElement(ReadOutlined),
          settings: createElement(SettingOutlined),
          templates: createElement(SnippetsOutlined),
          modules: createElement(DatabaseOutlined),
          home: createElement(HomeOutlined),
          dashboards: createElement(DashboardOutlined),
          variables: createElement(CodeOutlined),
          cloud: createElement(CloudOutlined)
        }
      : {
          docs: DocsIcon,
          settings: SettingsIcon,
          templates: TemplateIcon,
          modules: ModulesIcon,
          home: HomepageIcon,
          dashboards: DashboardIcon,
          variables: VariableIcon,
          cloud: CloudInsightsIcon
        };

    const can = (permission: RolesApi.RBACPermission | RBACPermission[]) => hasPermission(permission).isAuthorized;

    const getOrgAction = (id: string, overrides: Partial<Action>): Action => ({
      id,
      name: capitalize(id),
      keywords: id,
      perform: () => navigate(`/${id}`),
      section,
      ...overrides
    });

    const homepageAction = getOrgAction('home', { icon: icons.home, perform: () => navigate('/') });
    const dashboradAction = getOrgAction('dashboards', { icon: icons.dashboards });
    const variablesAction = getOrgAction('variables', { icon: icons.variables });
    const cloudCompassAction: Action = getOrgAction('cloud', {
      name: 'Cloud Compass',
      keywords: 'cloud compass',
      icon: icons.cloud
    });

    return compact([
      { ...docsAction, icon: icons.docs },
      homepageAction,
      can('VIEW_DASHBOARD') && dashboradAction,
      can('VIEW_DASHBOARD') && cloudCompassAction,
      variablesAction,
      ...generateTemplatesSectionActions(navigate, icons.templates),
      ...generateModulesSectionActions(navigate, icons.modules),
      can('EDIT_ORGANIZATION_SETTINGS') && { ...organizationSettingsBasicAction, icon: icons.settings }
    ]);
  }, [hasPermission, navigate, isNewDesign]);
}
